import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import arrowLeft from "../assets/images/navbar/arrow-left.png";
import { supportService } from "../services";
import { useAuth } from "../context/AuthContext";
import socketClient from "../utils/socketClient";
import { SOCKET_EVENTS } from "../utils/socketEvents";
import {
  SupportMessageType,
  isSupportMessageFromUser,
  mapSocketSupportMessage,
  mergeSupportMessages,
  sortSupportMessages,
} from "../utils/support";

const LIMIT = 20;

const formatMessageTime = (dateStr: string): string => {
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return "";

  const hours = d.getHours();
  const minutes = d.getMinutes().toString().padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  const h = hours % 12 || 12;
  return `${h}:${minutes} ${ampm}`;
};

const SupportChat = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { checkAuth, openSigninModal } = useAuth();

  const topic = searchParams.get("topic") || "";
  const orderId = searchParams.get("orderId") || "";
  const initialMessage = searchParams.get("initialMessage") || "";

  const [messages, setMessages] = useState<SupportMessageType[]>([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [sending, setSending] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [socketLive, setSocketLive] = useState(socketClient.isConnected());

  const isFetching = useRef(false);
  const listRef = useRef<HTMLDivElement>(null);
  const hasPrefixedTopic = useRef(false);
  const shouldStickToBottom = useRef(true);

  useEffect(() => {
    if (!checkAuth()) {
      openSigninModal();
      navigate("/help-and-support", { replace: true });
    }
  }, [checkAuth, navigate, openSigninModal]);

  useEffect(() => {
    if (hasPrefixedTopic.current) return;
    if (!topic && !orderId && !initialMessage) return;

    hasPrefixedTopic.current = true;
    const parts: string[] = [];
    if (topic) parts.push(topic);
    if (orderId) parts.push(`Order ID: ${orderId}`);
    if (initialMessage) parts.push(initialMessage);
    setText(parts.join("\n"));
  }, [topic, orderId, initialMessage]);

  const scrollToBottom = (smooth = false) => {
    const el = listRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: smooth ? "smooth" : "auto" });
  };

  const fetchMessages = useCallback(async (pageNum: number) => {
    if (isFetching.current) return;
    isFetching.current = true;

    if (pageNum === 1) setLoading(true);
    else setLoadingMore(true);

    try {
      const res: any = await supportService.getMessages(pageNum, LIMIT);
      if (res?.status && Array.isArray(res.data)) {
        if (pageNum === 1) {
          shouldStickToBottom.current = true;
        }

        setMessages((prev) => {
          const merged = pageNum === 1 ? res.data : [...res.data, ...prev];
          return mergeSupportMessages([], merged);
        });

        const { currentPage, totalPages } = res.pagination ?? {
          currentPage: pageNum,
          totalPages: pageNum,
        };
        setHasMore(currentPage < totalPages);
        setPage(currentPage);
      }
    } catch (error: any) {
      console.error("Failed to fetch support messages:", error);
      if (pageNum === 1) {
        toast.error(error?.message || "Unable to load support messages.");
      }
    } finally {
      if (pageNum === 1) setLoading(false);
      else setLoadingMore(false);
      isFetching.current = false;
    }
  }, []);

  const markRead = useCallback(async () => {
    try {
      await supportService.markAsRead();
    } catch (error) {
      console.error("Failed to mark support messages as read:", error);
    }
  }, []);

  const appendSocketMessage = useCallback(
    (payload: unknown) => {
      const incoming = mapSocketSupportMessage(payload);
      if (!incoming) return;

      setMessages((prev) => mergeSupportMessages(prev, [incoming]));
      shouldStickToBottom.current = true;

      if (!isSupportMessageFromUser(incoming)) {
        markRead();
      }
    },
    [markRead]
  );

  useEffect(() => {
    fetchMessages(1);
    markRead();
    socketClient.connect();
  }, [fetchMessages, markRead]);

  useEffect(() => {
    const onConnected = () => setSocketLive(true);
    const onDisconnect = () => setSocketLive(false);
    const onRoomReady = () => {
      setSocketLive(true);
      fetchMessages(1);
    };
    const onRead = () => {
      setMessages((prev) =>
        prev.map((msg) =>
          isSupportMessageFromUser(msg)
            ? { ...msg, isRead: true, readAt: msg.readAt || new Date().toISOString() }
            : msg
        )
      );
    };

    socketClient.on(SOCKET_EVENTS.CONNECTED, onConnected);
    socketClient.on("connect", onConnected);
    socketClient.on("disconnect", onDisconnect);
    socketClient.on(SOCKET_EVENTS.SUPPORT_ROOM_READY, onRoomReady);
    socketClient.on(SOCKET_EVENTS.SUPPORT_MESSAGE, appendSocketMessage);
    socketClient.on(SOCKET_EVENTS.SUPPORT_READ, onRead);

    return () => {
      socketClient.off(SOCKET_EVENTS.CONNECTED, onConnected);
      socketClient.off("connect", onConnected);
      socketClient.off("disconnect", onDisconnect);
      socketClient.off(SOCKET_EVENTS.SUPPORT_ROOM_READY, onRoomReady);
      socketClient.off(SOCKET_EVENTS.SUPPORT_MESSAGE, appendSocketMessage);
      socketClient.off(SOCKET_EVENTS.SUPPORT_READ, onRead);
    };
  }, [appendSocketMessage, fetchMessages]);

  useEffect(() => {
    if (shouldStickToBottom.current) {
      scrollToBottom(false);
    }
  }, [messages, loading]);

  const handleLoadMore = () => {
    if (!loadingMore && hasMore && !loading) {
      shouldStickToBottom.current = false;
      fetchMessages(page + 1);
    }
  };

  const handleSend = async () => {
    const body = text.trim();
    if (!body || sending) return;

    setSending(true);
    shouldStickToBottom.current = true;
    try {
      const res: any = await supportService.sendMessage({ body });
      if (res?.status) {
        setText("");
        if (res.data?._id) {
          setMessages((prev) => sortSupportMessages([...prev, res.data]));
        } else {
          await fetchMessages(1);
        }
        requestAnimationFrame(() => scrollToBottom(true));
      } else {
        toast.error(res?.message || "Failed to send message. Please try again.");
      }
    } catch (error: any) {
      console.error("Failed to send support message:", error);
      toast.error(error?.message || "Failed to send message. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="flex flex-col h-[calc(100dvh-5rem)] md:h-[calc(100dvh-72px)] bg-white dark:bg-gray-900">
      <div className="flex items-center gap-3 px-4 py-3 border-b dark:border-gray-700">
        <button
          type="button"
          onClick={() => navigate("/help-and-support")}
          className="w-10 h-10 flex items-center justify-center"
        >
          <img src={arrowLeft} alt="back" className="w-6 h-6 dark:invert" />
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="text-[#050505] dark:text-white font-urbanist text-[18px] font-semibold">
            Support Chat
          </h1>
          <p className="text-[12px] font-urbanist text-[#6A6A6A] dark:text-gray-400">
            {socketLive ? "Connected" : "Connecting..."}
          </p>
        </div>
        <span
          className={`h-2.5 w-2.5 rounded-full ${
            socketLive ? "bg-emerald-500" : "bg-gray-400"
          }`}
          aria-hidden
        />
      </div>

      <div ref={listRef} className="flex-1 overflow-y-auto px-4 py-4">
        {loading ? (
          <div className="h-full flex items-center justify-center">
            <p className="text-[#6A6A6A] dark:text-gray-400 font-urbanist text-sm">
              Loading messages...
            </p>
          </div>
        ) : messages.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-center px-6">
            <p className="text-3xl mb-2">💬</p>
            <p className="text-[#050505] dark:text-white font-urbanist text-lg font-semibold">
              How can we help?
            </p>
            <p className="text-[#6A6A6A] dark:text-gray-400 font-urbanist text-sm mt-1">
              Send a message and our support team will get back to you soon.
            </p>
          </div>
        ) : (
          <>
            {hasMore && (
              <div className="flex justify-center mb-3">
                <button
                  type="button"
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                  className="text-[#7402A7] dark:text-purple-400 font-urbanist text-sm font-semibold disabled:opacity-60"
                >
                  {loadingMore ? "Loading..." : "Load earlier messages"}
                </button>
              </div>
            )}
            {messages.map((item) => {
              const fromUser = isSupportMessageFromUser(item);
              return (
                <div
                  key={item._id}
                  className={`mb-2.5 flex ${fromUser ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[80%] rounded-2xl px-3.5 py-2.5 ${
                      fromUser
                        ? "bg-[#7402A7] text-white rounded-br-sm"
                        : "bg-white dark:bg-gray-800 border dark:border-gray-700 text-[#050505] dark:text-white rounded-bl-sm"
                    }`}
                  >
                    {!fromUser && (
                      <p className="text-[11px] font-urbanist font-semibold text-[#7402A7] dark:text-purple-300 mb-0.5">
                        Support
                      </p>
                    )}
                    <p className="font-urbanist text-sm whitespace-pre-wrap break-words">
                      {item.body}
                    </p>
                    <p
                      className={`text-[11px] font-urbanist text-right mt-1 ${
                        fromUser ? "text-white/70" : "text-[#8A8A8A] dark:text-gray-400"
                      }`}
                    >
                      {formatMessageTime(item.createdAt)}
                    </p>
                  </div>
                </div>
              );
            })}
          </>
        )}
      </div>

      <div className="flex items-end gap-2 px-4 pt-3 pb-4 border-t dark:border-gray-700 bg-white dark:bg-gray-900">
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
          placeholder="Type your message..."
          rows={1}
          className="flex-1 min-h-[44px] max-h-[120px] resize-none rounded-full border dark:border-gray-700 bg-[#F7F7F7] dark:bg-gray-800 px-4 py-2.5 text-sm font-urbanist text-[#050505] dark:text-white outline-none"
        />
        <button
          type="button"
          onClick={handleSend}
          disabled={sending || !text.trim()}
          className="h-11 w-11 rounded-full bg-[#7402A7] text-white font-urbanist text-sm font-semibold disabled:opacity-40"
          aria-label="Send message"
        >
          {sending ? "…" : "➤"}
        </button>
      </div>
    </div>
  );
};

export default SupportChat;
